import React from 'react';
import { Dialog } from '@headlessui/react';
import { XMarkIcon, SparklesIcon, TagIcon } from '@heroicons/react/24/outline';
import { useChangelog } from '../../../components/Changelog/ChangelogProvider';
import BaseModal from './BaseModal';

export { default as BaseModal } from './BaseModal';
export {
  ModalHeader,
  ModalBody,
  ModalFooter,
  ConfirmModal,
  ICON_TONE_CLASSES
} from './ModalLayout';

const TrialEndedModal = ({ open, setOpen }) => (
  <BaseModal isOpen={open} onClose={() => setOpen(false)} size="md">
    <div className="bg-white px-4 pb-4 pt-5 sm:p-6 sm:pb-4">
      <div className="sm:flex sm:items-start">
        <div className="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left">
          <Dialog.Title as="h3" className="text-base font-semibold leading-6 text-gray-900">
            Trial Period Ended
          </Dialog.Title>
          <div className="mt-2">
            <p className="text-sm text-gray-500">
              Your trial period has ended. To continue using our AI-powered content generation tools, please upgrade to a paid plan.
            </p>
          </div>
        </div>
      </div>
    </div>
    <div className="bg-gray-50 px-4 py-3 sm:flex sm:flex-row-reverse sm:px-6">
      <a
        href="/pricing"
        className="inline-flex w-full justify-center rounded-md bg-primary-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-primary-500 sm:ml-3 sm:w-auto"
      >
        Upgrade Now
      </a>
      <button
        type="button"
        className="mt-3 inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:mt-0 sm:w-auto"
        onClick={() => setOpen(false)}
      >
        Cancel
      </button>
    </div>
  </BaseModal>
);

const DeleteConfirmationModal = ({ open, setOpen, onConfirm, selectedCount, isDeleting }) => (
  <BaseModal isOpen={open} onClose={() => setOpen(false)} size="md">
    <div className="bg-white px-4 pb-4 pt-5 sm:p-6 sm:pb-4">
      <div className="sm:flex sm:items-start">
        <div className="mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
          <svg className="h-6 w-6 text-red-600" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
          </svg>
        </div>
        <div className="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left">
          <Dialog.Title as="h3" className="text-base font-semibold leading-6 text-gray-900">
            Delete Articles
          </Dialog.Title>
          <div className="mt-2">
            <p className="text-sm text-gray-500">
              Are you sure you want to delete {selectedCount} article{selectedCount !== 1 ? 's' : ''}? This action cannot be undone.
            </p>
          </div>
        </div>
      </div>
    </div>
    <div className="bg-gray-50 px-4 py-3 sm:flex sm:flex-row-reverse sm:px-6">
      <button
        type="button"
        onClick={onConfirm}
        disabled={isDeleting}
        className="inline-flex w-full justify-center rounded-md bg-red-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-red-500 disabled:opacity-50 disabled:cursor-not-allowed sm:ml-3 sm:w-auto"
      >
        {isDeleting ? 'Deleting...' : 'Delete'}
      </button>
      <button
        type="button"
        onClick={() => setOpen(false)}
        disabled={isDeleting}
        className="mt-3 inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed sm:mt-0 sm:w-auto"
      >
        Cancel
      </button>
    </div>
  </BaseModal>
);

const PublishWordPressConfirmationModal = ({ open, setOpen, onConfirm, isPublishing, articleTitle }) => (
  <BaseModal isOpen={open} onClose={() => setOpen(false)} size="md">
    <div className="bg-white px-4 pb-4 pt-5 sm:p-6 sm:pb-4">
      <div className="sm:flex sm:items-start">
        <div className="mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-gray-100 sm:mx-0 sm:h-10 sm:w-10">
          <svg className="h-6 w-6 text-gray-600" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
          </svg>
        </div>
        <div className="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left">
          <Dialog.Title as="h3" className="text-base font-semibold leading-6 text-gray-900">
            Publish to WordPress
          </Dialog.Title>
          <div className="mt-2">
            <p className="text-sm text-gray-500">
              Are you sure you want to publish "{articleTitle}" to WordPress? This will make the article available on your website.
            </p>
          </div>
        </div>
      </div>
    </div>
    <div className="bg-gray-50 px-4 py-3 sm:flex sm:flex-row-reverse sm:px-6">
      <button
        type="button"
        onClick={onConfirm}
        disabled={isPublishing}
        className="inline-flex w-full justify-center rounded-md bg-primary-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed sm:ml-3 sm:w-auto"
      >
        {isPublishing ? 'Publishing...' : 'Publish'}
      </button>
      <button
        type="button"
        onClick={() => setOpen(false)}
        disabled={isPublishing}
        className="mt-3 inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed sm:mt-0 sm:w-auto"
      >
        Cancel
      </button>
    </div>
  </BaseModal>
);

const ArchiveConfirmationModal = ({ open, setOpen, onConfirm, isArchiving, articleTitle }) => (
  <BaseModal isOpen={open} onClose={() => setOpen(false)} size="md">
    <div className="bg-white px-4 pb-4 pt-5 sm:p-6 sm:pb-4">
      <div className="sm:flex sm:items-start">
        <div className="mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-gray-100 sm:mx-0 sm:h-10 sm:w-10">
          <svg className="h-6 w-6 text-gray-600" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
          </svg>
        </div>
        <div className="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left">
          <Dialog.Title as="h3" className="text-base font-semibold leading-6 text-gray-900">
            Archive Article
          </Dialog.Title>
          <div className="mt-2">
            <p className="text-sm text-gray-500">
              Are you sure you want to archive "{articleTitle}"? The article will be hidden from the articles list but can be restored later.
            </p>
          </div>
        </div>
      </div>
    </div>
    <div className="bg-gray-50 px-4 py-3 sm:flex sm:flex-row-reverse sm:px-6">
      <button
        type="button"
        onClick={onConfirm}
        disabled={isArchiving}
        className="inline-flex w-full justify-center rounded-md bg-primary-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed sm:ml-3 sm:w-auto"
      >
        {isArchiving ? 'Archiving...' : 'Archive'}
      </button>
      <button
        type="button"
        onClick={() => setOpen(false)}
        disabled={isArchiving}
        className="mt-3 inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed sm:mt-0 sm:w-auto"
      >
        Cancel
      </button>
    </div>
  </BaseModal>
);

const DeleteDatasetConfirmationModal = ({ open, setOpen, onConfirm, isDeleting, datasetName }) => (
  <BaseModal isOpen={open} onClose={() => setOpen(false)} size="md">
    <div className="bg-white px-4 pb-4 pt-5 sm:p-6 sm:pb-4">
      <div className="sm:flex sm:items-start">
        <div className="mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
          <svg className="h-6 w-6 text-red-600" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
          </svg>
        </div>
        <div className="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left">
          <Dialog.Title as="h3" className="text-base font-semibold leading-6 text-gray-900">
            Remove Dataset
          </Dialog.Title>
          <div className="mt-2">
            <p className="text-sm text-gray-500">
              Are you sure you want to remove "{datasetName}"? This action cannot be undone.
            </p>
          </div>
        </div>
      </div>
    </div>
    <div className="bg-gray-50 px-4 py-3 sm:flex sm:flex-row-reverse sm:px-6">
      <button
        type="button"
        onClick={onConfirm}
        disabled={isDeleting}
        className="inline-flex w-full justify-center rounded-md bg-red-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-red-500 disabled:opacity-50 disabled:cursor-not-allowed sm:ml-3 sm:w-auto"
      >
        {isDeleting ? 'Removing...' : 'Remove'}
      </button>
      <button
        type="button"
        onClick={() => setOpen(false)}
        disabled={isDeleting}
        className="mt-3 inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed sm:mt-0 sm:w-auto"
      >
        Cancel
      </button>
    </div>
  </BaseModal>
);

const ChangelogModal = ({ isOpen, onClose }) => {
  const { latestChangelog, loading } = useChangelog();
  const changelog = latestChangelog;

  const getCategoryColor = (category) => {
    const colors = {
      Articles: 'bg-primary-100 text-primary-800',
      Features: 'bg-green-100 text-green-800',
      Improvements: 'bg-yellow-100 text-yellow-800',
      'Bug Fixes': 'bg-red-100 text-red-800',
      API: 'bg-purple-100 text-purple-800',
      'UI/UX': 'bg-pink-100 text-pink-800'
    };
    return colors[category] || 'bg-gray-100 text-gray-800';
  };

  return (
    <BaseModal isOpen={isOpen} onClose={onClose} size="lg" closeOnBackdrop={true}>
      <div className="bg-gradient-to-r from-primary-600 to-primary-700 px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-white bg-opacity-20">
              <SparklesIcon className="h-6 w-6 text-white" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-white">Co nowego w Prose Captain?</h3>
              <p className="text-primary-100 text-sm">Najnowsze aktualizacje i ulepszenia</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="rounded-full p-2 text-white hover:bg-white hover:bg-opacity-20 transition-colors"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>
      </div>

      <div className="px-6 py-6">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
            <span className="ml-3 text-gray-600">Ładowanie changelogu...</span>
          </div>
        ) : changelog ? (
          <div className="space-y-6">
            {changelog.changelog_thumbnail && (
              <div className="relative rounded-lg overflow-hidden bg-gray-100">
                <img
                  src={`https://panel.prosecaptain.com/assets/${changelog.changelog_thumbnail}?width=600&height=450&fit=cover`}
                  alt="Changelog thumbnail"
                  className="w-full h-64 object-cover"
                  style={{ aspectRatio: '4/3' }}
                />
                {changelog.changelog_category && changelog.changelog_category.length > 0 && (
                  <div className="absolute top-4 left-4">
                    <span
                      className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${getCategoryColor(changelog.changelog_category[0])}`}
                    >
                      <TagIcon className="h-4 w-4 mr-1" />
                      {changelog.changelog_category[0]}
                    </span>
                  </div>
                )}
              </div>
            )}

            {changelog.changelog_excerpt && (
              <div className="p-4 rounded-r-lg">
                <div
                  className="text-gray-800 font-medium leading-relaxed"
                  dangerouslySetInnerHTML={{ __html: changelog.changelog_excerpt }}
                />
              </div>
            )}
          </div>
        ) : (
          <div className="text-center py-12">
            <div className="mx-auto w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
              <SparklesIcon className="w-8 h-8 text-gray-400" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">Brak aktualizacji</h3>
            <p className="text-gray-500">Nie znaleziono żadnych wpisów w changelogu.</p>
          </div>
        )}
      </div>

      <div className="bg-gray-50 px-6 py-4">
        <div className="flex justify-between">
          {changelog && changelog.changelog_link ? (
            <a
              href={changelog.changelog_link}
              target="_blank"
              rel="noopener noreferrer"
              className="btn btn-sm btn-square btn-primary-outline"
              onClick={() => {
                const modalSeenIds = JSON.parse(localStorage.getItem('modal_seen_changelog_ids') || '[]');
                if (!modalSeenIds.includes(changelog.id)) {
                  modalSeenIds.push(changelog.id);
                  localStorage.setItem('modal_seen_changelog_ids', JSON.stringify(modalSeenIds));
                }
                onClose();
              }}
            >
              Learn More
            </a>
          ) : (
            <div></div>
          )}
          <button
            onClick={() => {
              if (changelog) {
                const modalSeenIds = JSON.parse(localStorage.getItem('modal_seen_changelog_ids') || '[]');
                if (!modalSeenIds.includes(changelog.id)) {
                  modalSeenIds.push(changelog.id);
                  localStorage.setItem('modal_seen_changelog_ids', JSON.stringify(modalSeenIds));
                }
              }
              onClose();
            }}
            className="btn btn-sm btn-square btn-secondary"
          >
            Zamknij
          </button>
        </div>
      </div>
    </BaseModal>
  );
};

export {
  TrialEndedModal,
  DeleteConfirmationModal,
  PublishWordPressConfirmationModal,
  ArchiveConfirmationModal,
  DeleteDatasetConfirmationModal,
  ChangelogModal
};

export default BaseModal;
